import { useEffect, useRef, useState, type KeyboardEvent, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { cn } from '~/lib/utils';
import { Tip } from '~/components/ui/tip';

/**
 * Pure: decide whether an edited name is worth saving. Returns the trimmed name
 * to persist, or null for a no-op — an empty/whitespace name, or one unchanged
 * from the current value (treating null as ''). Keeps the save from firing on a
 * click-and-blur or a cleared field. Exported for tests.
 */
export function normalizeRename(raw: string, current: string | null): string | null {
  const next = raw.trim();
  if (!next || next === (current ?? '')) return null;
  return next;
}

interface InlineRenameTitleProps {
  /** The current name. Null renders {@link fallback} as placeholder text. */
  value: string | null;
  /** Persist the new name. Should reject on failure so the error toast fires. */
  onSave: (next: string) => Promise<unknown>;
  /**
   * When set, the resting (non-editing) name is a button that runs this — for
   * lists where clicking the name opens the item. When omitted the name is
   * plain, non-interactive text: cards that stretch a separate open-button
   * across the whole card pass nothing here so the name lets clicks fall through
   * (`pointer-events-none`). Either way the pencil and the edit field stay
   * interactive.
   */
  onOpen?: () => void;
  /** Display text when {@link value} is null. */
  fallback?: string;
  /** Toast on a successful save. */
  successMessage?: string;
  /** Toast when {@link onSave} rejects without a message. */
  failureMessage?: string;
  maxLength?: number;
  className?: string;
  /**
   * Whether the resting name underlines on hover (only applies with {@link onOpen}).
   * Defaults true. Surfaces that already signal clickability another way (e.g. the
   * Work rows, which highlight the whole row) can pass false to drop the underline.
   */
  underlineOnHover?: boolean;
  /** FRONT-1501: optional `data-testid`s for a browser check to target a
   *  SPECIFIC item's rename affordance in a list of many (e.g. one gallery
   *  card) — the aria-labels already carry the item's name, but that isn't
   *  guaranteed unique across a list. Omitted entirely by default; no other
   *  caller needs to change. */
  triggerTestId?: string;
  inputTestId?: string;
  /** FRONT-3152: clicking the resting name itself starts the rename, for a
   *  surface whose name is a heading rather than a list row (the chat
   *  window's header). Ignored when {@link onOpen} is set. */
  clickToEdit?: boolean;
  /** Overrides the resting name's (and the edit field's) text size and
   *  weight, which default to the list size. */
  textClassName?: string;
  /** FRONT-3152: edit in a wrapping field that opens over the name and grows
   *  downwards, so a long name is shown in full while editing — for a name
   *  that sits in a fixed-width slot. Enter still saves; names stay one line. */
  editInPopover?: boolean;
}

/** Where the popover field opens: over the resting name, as wide as the
 *  viewport allows (16px margin), never narrower than the name itself. */
export function popoverFieldBox(
  anchor: { left: number; top: number; width: number },
  viewportWidth: number,
): { left: number; top: number; width: number } {
  const margin = 16;
  const left = Math.max(margin, Math.min(anchor.left - 8, viewportWidth - margin - 240));
  const width = Math.max(Math.min(576, viewportWidth - left - margin), Math.min(anchor.width + 16, viewportWidth - 2 * margin));
  return { left, top: anchor.top - 6, width };
}

type Anchor = { left: number; top: number; width: number };

/** The shared field props both editing variants take. */
interface RenameFieldProps {
  text: string;
  setText: (next: string) => void;
  onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  onBlur: () => void;
  saving: boolean;
  maxLength: number;
  testId?: string;
  textClassName?: string;
}

/** Focus the field and select its text once, when it mounts. */
function useFocusOnMount(ref: RefObject<HTMLInputElement | HTMLTextAreaElement | null>) {
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, [ref]);
}

/**
 * The editing state and the save/cancel rules, shared by every way the control
 * renders. Escape sets a flag so the blur it triggers does not also save.
 */
function useRenameEditor(
  value: string | null,
  onSave: (next: string) => Promise<unknown>,
  successMessage: string,
  failureMessage: string,
) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value ?? '');
  const [saving, setSaving] = useState(false);
  const skipBlurRef = useRef(false);

  const start = () => {
    setText(value ?? '');
    setEditing(true);
  };

  const commit = async () => {
    const next = normalizeRename(text, value);
    // No-op renames (empty, or unchanged) just close the field — never a request.
    if (next === null) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(next);
      toast.success(successMessage);
      setEditing(false);
    } catch (err) {
      toast.error((err as Error)?.message || failureMessage);
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void commit();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      skipBlurRef.current = true;
      setEditing(false);
    }
  };

  const onBlur = () => {
    if (skipBlurRef.current) {
      skipBlurRef.current = false;
      return;
    }
    void commit();
  };

  return { editing, text, setText, saving, start, onKeyDown, onBlur };
}

/** The in-place single-line field the list rows use. */
function InlineRenameField({ className, ...f }: RenameFieldProps & { className?: string }) {
  const ref = useRef<HTMLInputElement>(null);
  useFocusOnMount(ref);
  return (
    <input
      ref={ref}
      data-testid={f.testId}
      // FRONT-776 code-review round 3, finding 3: a card ancestor can now
      // be `draggable` (the Files-screen drag-to-move cards) — without
      // this, starting a mouse drag INSIDE this field (e.g. to select
      // text) is hijacked by the ancestor's native drag gesture instead of
      // selecting text, in both Chrome and Safari. `draggable={false}`
      // tells the browser this element opts out of the ancestor's drag.
      draggable={false}
      value={f.text}
      onChange={(e) => f.setText(e.target.value)}
      onKeyDown={f.onKeyDown}
      onBlur={f.onBlur}
      disabled={f.saving}
      maxLength={f.maxLength}
      aria-label="Name"
      className={cn(
        'pointer-events-auto min-w-0 flex-1 rounded border border-primary/40 bg-background px-1.5 py-0.5 text-sm font-medium text-foreground outline-none focus:border-primary',
        f.textClassName,
        className,
      )}
    />
  );
}

/**
 * FRONT-3152: the wrapping field that opens over a name in a fixed-width slot
 * and grows downwards to fit, so the whole name is visible while editing.
 * Portaled to the body because that slot clips its overflow.
 */
function PopoverRenameField({ anchor, ...f }: RenameFieldProps & { anchor: Anchor }) {
  const ref = useRef<HTMLTextAreaElement>(null);
  useFocusOnMount(ref);
  useEffect(() => {
    const area = ref.current;
    if (!area) return;
    area.style.height = 'auto';
    // scrollHeight excludes the border, which the border-box height includes.
    area.style.height = `${area.scrollHeight + area.offsetHeight - area.clientHeight}px`;
  }, [f.text]);
  const box = popoverFieldBox(anchor, window.innerWidth);
  return createPortal(
    <textarea
      ref={ref}
      data-testid={f.testId}
      rows={1}
      value={f.text}
      // A name is one line: a pasted line break becomes a space.
      onChange={(e) => f.setText(e.target.value.replace(/[\r\n]+/g, ' '))}
      onKeyDown={f.onKeyDown}
      onBlur={f.onBlur}
      disabled={f.saving}
      maxLength={f.maxLength}
      aria-label="Name"
      style={{ position: 'fixed', left: box.left, top: box.top, width: box.width }}
      className={cn(
        'z-[60] resize-none overflow-hidden rounded-md border border-primary/40 bg-background px-2 py-1 text-sm font-medium text-foreground shadow-lg outline-none focus:border-primary',
        f.textClassName,
      )}
    />,
    document.body,
  );
}

/** The resting name: a button that opens the item, a button that starts the
 *  rename (FRONT-3152), or plain text. */
function RestingName({
  label,
  onOpen,
  underlineOnHover,
  onStart,
  nameRef,
  testId,
  textClassName,
}: {
  label: string;
  onOpen?: () => void;
  underlineOnHover: boolean;
  onStart?: () => void;
  nameRef: RefObject<HTMLButtonElement | null>;
  testId?: string;
  textClassName?: string;
}) {
  if (onOpen) {
    return (
      <button
        onClick={onOpen}
        className={cn(
          'pointer-events-auto min-w-0 flex-1 truncate text-left text-sm font-medium text-foreground',
          underlineOnHover && 'hover:underline',
        )}
      >
        {label}
      </button>
    );
  }
  if (onStart) {
    return (
      <button
        ref={nameRef}
        type="button"
        data-testid={testId}
        onClick={onStart}
        className={cn(
          'pointer-events-auto min-w-0 flex-1 cursor-text truncate rounded px-1 -mx-1 py-1 -my-1 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted',
          textClassName,
        )}
      >
        {label}
      </button>
    );
  }
  return (
    <span className={cn('pointer-events-none min-w-0 flex-1 truncate text-sm font-medium text-foreground', textClassName)}>
      {label}
    </span>
  );
}

/**
 * Inline rename (FRONT-545 / FRONT-590). The shared control for renaming from a
 * LIST — chats and files alike, so the icon and the interaction are identical
 * across them. A pencil appears on the card's hover; clicking it turns the name
 * into an in-place text field — no dialog. Enter or blur saves, Escape cancels.
 * The caller owns persistence + cache invalidation via {@link onSave}; this
 * control owns the editing UX and the toasts.
 *
 * One deliberate non-user: the inbox reader's edit mode (FRONT-1107) renames
 * from its own heading field instead. Blur-to-save is wrong there, because the
 * blur that matters is the user clicking into the body text area right below —
 * that edit is committed by an explicit Save covering the name and body
 * together, not by moving focus.
 */
export function InlineRenameTitle({
  value,
  onSave,
  onOpen,
  fallback = 'Untitled',
  successMessage = 'Renamed',
  failureMessage = 'Rename failed',
  maxLength = 200,
  className,
  underlineOnHover = true,
  triggerTestId,
  inputTestId,
  clickToEdit = false,
  textClassName,
  editInPopover = false,
}: InlineRenameTitleProps) {
  const editor = useRenameEditor(value, onSave, successMessage, failureMessage);
  const nameRef = useRef<HTMLButtonElement>(null);
  const [anchor, setAnchor] = useState<Anchor | null>(null);
  const label = value ?? fallback;

  const start = () => {
    if (editInPopover && nameRef.current) {
      const r = nameRef.current.getBoundingClientRect();
      setAnchor({ left: r.left, top: r.top, width: r.width });
    }
    editor.start();
  };

  const field: RenameFieldProps = {
    text: editor.text,
    setText: editor.setText,
    onKeyDown: editor.onKeyDown,
    onBlur: editor.onBlur,
    saving: editor.saving,
    maxLength,
    testId: inputTestId,
    textClassName,
  };

  if (editor.editing && editInPopover && anchor) {
    // The resting name stays behind the popover, invisible, so the row does
    // not shift while editing.
    return (
      <>
        <span className={cn('inline-flex min-w-0 items-center gap-1', className)}>
          <span className={cn('invisible min-w-0 flex-1 truncate text-sm font-medium', textClassName)}>{label}</span>
        </span>
        <PopoverRenameField anchor={anchor} {...field} />
      </>
    );
  }

  if (editor.editing) return <InlineRenameField className={className} {...field} />;

  return (
    <span className={cn('inline-flex min-w-0 items-center gap-1', className)}>
      <RestingName
        label={label}
        onOpen={onOpen}
        underlineOnHover={underlineOnHover}
        onStart={clickToEdit ? start : undefined}
        nameRef={nameRef}
        testId={triggerTestId ? `${triggerTestId}-name` : undefined}
        textClassName={textClassName}
      />
      <Tip label="Rename">
        <button
          type="button"
          data-testid={triggerTestId}
          onClick={(e) => {
            e.stopPropagation();
            start();
          }}
          className="pointer-events-auto shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
          aria-label={`Rename ${label}`}
        >
          <PencilSimple size={13} />
        </button>
      </Tip>
    </span>
  );
}
